import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import React, { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import useAuth from "../../hooks/useAuth";
import { BsEye, BsEyeSlash } from "react-icons/bs";
import { useTranslation } from "react-i18next";
import { loginSchema } from "../../schema/auth";
import { Link } from "react-router-dom";
import { FaUserPlus } from "react-icons/fa";
import GoogleLoginButton from "../../components/GoogleLoginButton";
import TurnstileWidget from "../../components/TurnstileWidget";
import { registrationSiteKey } from "../../lib/turnstile";

const Login = () => {
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [resetChallenge, setResetChallenge] = useState(0);
  const siteKey = registrationSiteKey(window.location.hostname, process.env.REACT_APP_TURNSTILE_SITE_KEY);

  const { login } = useAuth();
  const { t, i18n } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(loginSchema),
  });

  const showpasswordIcon = (
    <span onClick={() => setShowPassword(!showPassword)}>
      {showPassword ? (
        <BsEye size={20} className="cursor-pointer" />
      ) : (
        <BsEyeSlash size={20} className="cursor-pointer" />
      )}
    </span>
  );

  const rememberMeHandler = () => {
    setRememberMe(!rememberMe);
    localStorage.setItem("remember_me", !rememberMe);
  };

  const onSubmit = async (data) => {
    try {
      if (!captchaToken) return toast.error("Complete the security check");
      await login(data.email, data.password, rememberMe, captchaToken);

      // reset();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setCaptchaToken("");
      setResetChallenge((value) => value + 1);
    }
  };

  return (
    <section className="flex min-h-screen items-center justify-center px-4 py-10 sm:px-6 sm:py-14 lg:py-20">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="relative mx-auto flex w-full max-w-md flex-col rounded-2xl bg-white text-gray-700 shadow-xl shadow-gray-900/10 bg-clip-border"
      >
        <div className="relative mx-5 -mt-6 mb-5 grid h-28 place-items-center overflow-hidden rounded-2xl bg-gradient-to-tr from-indigo-900 to-indigo-800 text-center text-white shadow-lg shadow-gray-900/20 bg-clip-border">
          <h3 className="block font-sans text-3xl antialiased font-semibold leading-snug tracking-normal text-white">
            {t("sign_in")}
            <p className="text-sm text-center mt-2 tracking-wide">
              {t("app-name")}
            </p>
          </h3>
        </div>
        <div className="flex flex-col gap-4 px-6 pb-2 pt-1 sm:px-8">
          <div className="relative h-11 w-full min-w-[200px] mb-3">
            <input
              {...register("email")}
              className={`w-full h-full px-3 py-3 font-sans text-sm font-normal transition-all bg-transparent border rounded-md peer border-t-transparent text-blue-gray-700 outline outline-0 placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 focus:border-2  focus:border-t-transparent focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 
                            ${
                              errors.email
                                ? "focus:border-red-500"
                                : "focus:border-gray-900"
                            }`}
              placeholder=""
            />
            {errors.email && (
              <p className="text-xs text-red-500 px-1 my-1">
                {t("validation.email_required")}
              </p>
            )}
            <label
              type="email"
              className={`before:content[' '] after:content[' '] pointer-events-none absolute left-0 -top-1.5 flex h-full w-full select-none !overflow-visible truncate text-[11px] font-normal leading-tight text-gray-500 transition-all before:pointer-events-none before:mt-[6.5px] before:mr-1 before:box-border before:block before:h-1.5 before:w-2.5 before:rounded-tl-md before:border-t before:border-l before:border-transparent before:transition-all after:pointer-events-none after:mt-[6.5px] after:ml-1 after:box-border after:block after:h-1.5 after:w-2.5 after:flex-grow after:rounded-tr-md after:border-t after:border-r after:border-transparent after:transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:leading-[4.1] peer-placeholder-shown:text-blue-gray-500 peer-placeholder-shown:before:border-transparent peer-placeholder-shown:after:border-transparent peer-focus:text-[11px] peer-focus:leading-tight peer-focus:text-gray-900 peer-focus:before:border-t-2 peer-focus:before:border-l-2  peer-focus:after:border-t-2 peer-focus:after:border-r-2 peer-focus:after:!border-gray-900 peer-disabled:text-transparent peer-disabled:before:border-transparent peer-disabled:after:border-transparent peer-disabled:peer-placeholder-shown:text-blue-gray-500
                            ${
                              errors.email
                                ? "peer-focus:text-red-500 peer-focus:after:!border-red-500 before:border-blue-gray-200"
                                : ""
                            }
                            `}
            >
              {t("form.your-email")}
            </label>
          </div>
          <div className="relative h-11 w-full min-w-[200px] mb-3">
            <input
              {...register("password")}
              type={showPassword ? "text" : "password"}
              className={`w-full h-full px-3 py-3 font-sans text-sm font-normal transition-all bg-transparent border rounded-md peer border-t-transparent text-blue-gray-700 outline outline-0 placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 focus:border-2  focus:border-t-transparent focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 
                            ${
                              errors.password
                                ? "focus:border-red-500"
                                : "focus:border-gray-900"
                            }`}
              placeholder=""
            />
            <i
              className={`absolute ${
                i18n.language === "en" ? "right-4" : "left-4"
              } top-3`}
            >
              {showpasswordIcon}
            </i>
            {errors.password && (
              <p className="text-xs text-red-500 px-1 my-1">
                {t("validation.password_required")}
              </p>
            )}
            <label
              className={`before:content[' '] after:content[' '] pointer-events-none absolute left-0 -top-1.5 flex h-full w-full select-none !overflow-visible truncate text-[11px] font-normal leading-tight text-gray-500 transition-all before:pointer-events-none before:mt-[6.5px] before:mr-1 before:box-border before:block before:h-1.5 before:w-2.5 before:rounded-tl-md before:border-t before:border-l before:border-transparent before:transition-all after:pointer-events-none after:mt-[6.5px] after:ml-1 after:box-border after:block after:h-1.5 after:w-2.5 after:flex-grow after:rounded-tr-md after:border-t after:border-r after:border-transparent after:transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:leading-[4.1] peer-placeholder-shown:text-blue-gray-500 peer-placeholder-shown:before:border-transparent peer-placeholder-shown:after:border-transparent peer-focus:text-[11px] peer-focus:leading-tight peer-focus:text-gray-900 peer-focus:before:border-t-2 peer-focus:before:border-l-2  peer-focus:after:border-t-2 peer-focus:after:border-r-2 peer-focus:after:!border-gray-900 peer-disabled:text-transparent peer-disabled:before:border-transparent peer-disabled:after:border-transparent peer-disabled:peer-placeholder-shown:text-blue-gray-500
                            ${
                              errors.password
                                ? "peer-focus:text-red-500 peer-focus:after:!border-red-500 before:border-blue-gray-200"
                                : ""
                            }
                            `}
            >
              {t("form.your-password")}
            </label>
            <div className="flex justify-end mt-1">
              <Link
                to="/forgot-pwd"
                className="text-center text-blue-gray-800 text-sm font-semibold hover:underline transition-shadow capitalize"
              >
                {t("forgot_password")}
              </Link>
            </div>
          </div>
          <div className="-ml-2.5">
            <div className="inline-flex items-center">
              <label
                htmlFor="checkbox"
                className="relative flex items-center p-3 rounded-full cursor-pointer"
              >
                <input
                  type="checkbox"
                  className="before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-md border border-gray-200 transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-gray-500 before:opacity-0 before:transition-opacity checked:gray-indigo-900 checked:bg-indigo-900 checked:before:bg-indigo-900 hover:before:opacity-10"
                  id="checkbox"
                  checked={rememberMe}
                  onChange={rememberMeHandler}
                />
                <span className="absolute text-white transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3.5 w-3.5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    stroke="currentColor"
                    strokeWidth="1"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                </span>
              </label>
              <label
                className="mt-px font-light text-gray-700 cursor-pointer select-none"
                htmlFor="checkbox"
              >
                {t("remember_me")}
              </label>
            </div>
          </div>
        </div>
        <div className="px-6 pb-7 pt-3 sm:px-8 sm:pb-8">
          {siteKey ? <TurnstileWidget siteKey={siteKey} action="login"
            onTokenChange={setCaptchaToken} resetSignal={resetChallenge} />
            : <p role="alert" className="mb-3 text-sm text-red-700">Login security check is unavailable.</p>}
          <button
            className="block w-full select-none rounded-lg bg-gradient-to-tr from-indigo-900 to-indigo-800 py-3 px-6 text-center align-middle font-sans text-xs font-bold uppercase text-white shadow-md shadow-indigo-900/10 transition-all hover:shadow-lg hover:shadow-indigo-900/20 active:opacity-[0.85] disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none mb-3"
            type="submit"
            disabled={isSubmitting || !captchaToken}
          >
            {isSubmitting ? `${t("authorizing")}...` : t("sign_in")}
          </button>

          <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-gray-400">
            <span className="h-px flex-1 bg-gray-200" />
            <span>or</span>
            <span className="h-px flex-1 bg-gray-200" />
          </div>

          <GoogleLoginButton label="Sign in quickly with Google" />

          <div className="flex justify-center">
            <Link
              to="/sign-up"
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white border border-gray-200 text-sm font-medium text-gray-800 shadow-sm hover:shadow-md transition-shadow duration-150 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-primary-300 mt-6"
              aria-label={t("dont_have_an_account")}
            >
              <FaUserPlus className="text-primary-600" />
              <span>{t("dont_have_an_account")}</span>
            </Link>
          </div>
        </div>
      </form>
    </section>
  );
};

export default Login;
